import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'

import { MatTableDataSource } from '@angular/material/table'
import { BankBalance, BankBalancesPerMonth } from '@lemon/core-firebase/model/tiliote'
import { AsiakasService, AsiakkaanAvainTiedot } from '../../_angular/service/asiakas/asiakas.service'

import { MatSort } from '@angular/material/sort'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export interface BankBalanceListaus extends BankBalance {
  iban: string
}

interface PaivasaldoLomake {
  monthRange: number | null
}

@Component({
  templateUrl: './paivasaldot-listaus.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaivasaldoListausComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  @ViewChild(MatSort, { static: true }) _sort: MatSort

  lomake: FieldTree<PaivasaldoLomake>
  private readonly _injector = inject(Injector)
  dataSource = new MatTableDataSource<BankBalanceListaus>([])
  naytettavatKolumnit: string[] = ['paiva', 'iban', 'saldo', 'edited']
  lataaObservable: BehaviorSubject<boolean> = new BehaviorSubject(true)
  lataaPaivaJaIban: BehaviorSubject<string> = new BehaviorSubject('')

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _asiakasService: AsiakasService,
    private _dateService: DateService,
    private _kirjanpitoUriService: KirjanpitoUriService,
  ) {}

  ngOnInit() {
    this.lomake = form(signal<PaivasaldoLomake>({ monthRange: null }), { injector: this._injector })

    const today = this._dateService.currentLocalDate()

    this.dataSource.sort = this._sort
    this.dataSource.sortData = (data: BankBalanceListaus[], sort: MatSort): BankBalanceListaus[] => {
      const active = sort.active
      const direction = sort.direction

      if (!active || direction === '') {
        return data
      }

      const directionMultiplier = direction === 'asc' ? 1 : -1
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

      if (active === 'iban') {
        return data.sort((a, b) => {
          return collator.compare(a.iban, b.iban) * directionMultiplier
        })
      }
      if (active === 'paiva') {
        return data.sort((a, b) => {
          return (a.paiva - b.paiva) * directionMultiplier
        })
      }
      if (active === 'saldo') {
        return data.sort((a, b) => {
          return (a.saldo - b.saldo) * directionMultiplier
        })
      }
      if (active === 'edited') {
        return data.sort((a, b) => {
          return (a.edited.toMillis() - b.edited.toMillis()) * directionMultiplier
        })
      }
      return data
    }
    this._sort.active = 'paiva'
    this._sort.direction = 'desc'

    arvonMuutokset(this.lomake.monthRange, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async monthRange => {
        this.lataaObservable.next(true)

        this.dataSource.data = []

        const startDate = this._dateService.lisaaKuukausiaPaikallinen(today, -monthRange)
        const startMonthNum = this._dateService.localMonthToNumber({ year: startDate.year, month: startDate.month })

        const asiakasAvainTiedot: AsiakkaanAvainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
        const uri = this._kirjanpitoUriService.annaBankTransactionsClosingBalancesCollectionUri(asiakasAvainTiedot.avain)

        const kuukausidenLoppusaldot: BankBalancesPerMonth[] = await this._firebaseLemonaid.firestoreCollection<BankBalancesPerMonth>(uri).where('kuukausi', '>=', startMonthNum).get()

        for (const kuukaudenSaldot of kuukausidenLoppusaldot) {
          const listausdata: BankBalanceListaus[] = Object.values(kuukaudenSaldot?.saldot || {}).map(a => {
            const b = a as BankBalanceListaus
            b.iban = kuukaudenSaldot.iban
            return b
          })
          this.dataSource.data = this.dataSource.data.concat(listausdata)
        }
        this.lataaObservable.next(false)
      })

    asetaArvo(this.lomake.monthRange, 1) // Set 1 month as starting value
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  get saldoTiedotDatasource(): MatTableDataSource<BankBalanceListaus> {
    return this.dataSource
  }
}
